// Theme manager for dark mode functionality
const THEME_KEY = 'plex-theme';
const THEMES = {
  LIGHT: 'light',
  DARK: 'dark'
};

/**
 * Get the current theme from localStorage or system preference
 * @returns {'light' | 'dark'}
 */
export function getTheme() {
  // Check localStorage first
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === THEMES.LIGHT || stored === THEMES.DARK) {
    return stored;
  }

  // Fall back to system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return THEMES.DARK;
  }

  return THEMES.LIGHT;
}

/**
 * Set the theme and persist to localStorage
 * @param {'light' | 'dark'} theme
 */
export function setTheme(theme) {
  if (theme !== THEMES.LIGHT && theme !== THEMES.DARK) {
    console.error(`Invalid theme: ${theme}`);
    return;
  }

  // Update localStorage
  localStorage.setItem(THEME_KEY, theme);

  // Apply to DOM
  applyTheme(theme);
}

/**
 * Apply theme to DOM by setting data-theme attribute
 * @param {'light' | 'dark'} theme
 */
function applyTheme(theme) {
  const html = document.documentElement;
  if (theme === THEMES.DARK) {
    html.setAttribute('data-theme', 'dark');
  } else {
    html.removeAttribute('data-theme');
  }
}

/**
 * Toggle between light and dark themes
 */
export function toggleTheme() {
  const current = getTheme();
  const next = current === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT;
  setTheme(next);
}

/**
 * Initialize theme on app load
 * Should be called before React renders to prevent flash
 */
export function initTheme() {
  const theme = getTheme();
  applyTheme(theme);

  // Watch system preference changes only if user hasn't set preference
  const stored = localStorage.getItem(THEME_KEY);
  if (!stored) {
    watchSystemPreference();
  }
}

/**
 * Watch for system preference changes and apply automatically
 * Only active when user hasn't explicitly set a preference
 */
function watchSystemPreference() {
  if (!window.matchMedia) return;

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handleChange = (e) => {
    // Only apply if user hasn't set preference
    const stored = localStorage.getItem(THEME_KEY);
    if (!stored) {
      const theme = e.matches ? THEMES.DARK : THEMES.LIGHT;
      applyTheme(theme);
    }
  };

  // Modern API
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleChange);
  } else {
    // Legacy API
    mediaQuery.addListener(handleChange);
  }
}

export { THEMES };